import type { ColoredMaterialResult } from "@color-finder/color-finder-api/models";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from "react";
import type {
  AnalysisProgress,
  ExploreImageState,
  ExtractResponse,
  FlowStep,
  Region,
} from "./types";

type Action =
  | {
      type: "SET_UPLOADED_IMAGE";
      payload: {
        image: string;
        file: File;
        naturalSize: { width: number; height: number };
      };
    }
  | { type: "SET_REGIONS"; payload: Region[] }
  | { type: "ADD_REGION"; payload: { region: Region; max: number } }
  | { type: "UPDATE_REGION"; payload: { id: string; partial: Partial<Region> } }
  | { type: "REMOVE_REGION"; payload: string }
  | { type: "SET_FLOW_STEP"; payload: FlowStep }
  | { type: "SET_ANALYSIS_PROGRESS"; payload: AnalysisProgress[] }
  | {
      type: "SET_EXTRACT_RESULTS";
      payload: {
        extractResults: ExtractResponse;
        initialSearchResult: ColoredMaterialResult | null;
        initialSearchKey: string | null;
      };
    }
  | { type: "SET_ERROR"; payload: string }
  | { type: "DISCARD" };

const initialState: ExploreImageState = {
  flowStep: "idle",
  uploadedImage: null,
  uploadedFile: null,
  imageNaturalSize: null,
  regions: [],
  analysisProgress: [],
  extractResults: null,
  initialSearchResult: null,
  initialSearchKey: null,
  error: null,
};

function reducer(state: ExploreImageState, action: Action): ExploreImageState {
  switch (action.type) {
    case "SET_UPLOADED_IMAGE":
      if (state.uploadedImage) {
        URL.revokeObjectURL(state.uploadedImage);
      }
      // A new image starts a fresh session — clear regions/analysis/extract
      // results left over from any previous upload (e.g. re-uploading via
      // the compact uploader after a CX-2908 discard) so the new
      // region-selection screen never carries stale state.
      return {
        ...initialState,
        uploadedImage: action.payload.image,
        uploadedFile: action.payload.file,
        imageNaturalSize: action.payload.naturalSize,
        flowStep: "region-selection",
      };
    case "SET_REGIONS":
      return { ...state, regions: action.payload };
    case "ADD_REGION": {
      if (state.regions.length >= action.payload.max) return state;
      return { ...state, regions: [...state.regions, action.payload.region] };
    }
    case "UPDATE_REGION":
      return {
        ...state,
        regions: state.regions.map((r) =>
          r.id === action.payload.id ? { ...r, ...action.payload.partial } : r,
        ),
      };
    case "REMOVE_REGION":
      return {
        ...state,
        regions: state.regions.filter((r) => r.id !== action.payload),
      };
    case "SET_FLOW_STEP":
      return { ...state, flowStep: action.payload };
    case "SET_ANALYSIS_PROGRESS":
      return { ...state, analysisProgress: action.payload };
    case "SET_EXTRACT_RESULTS":
      return {
        ...state,
        extractResults: action.payload.extractResults,
        initialSearchResult: action.payload.initialSearchResult,
        initialSearchKey: action.payload.initialSearchKey,
        flowStep: "results",
      };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "DISCARD":
      if (state.uploadedImage) {
        URL.revokeObjectURL(state.uploadedImage);
      }
      return { ...initialState };
    default:
      return state;
  }
}

interface ExploreImageContextValue {
  state: ExploreImageState;
  setUploadedImage: (
    file: File,
    image: string,
    naturalSize: { width: number; height: number },
  ) => void;
  setRegions: (regions: Region[]) => void;
  addRegion: (region: Region, max: number) => void;
  updateRegion: (id: string, partial: Partial<Region>) => void;
  removeRegion: (id: string) => void;
  confirmRegions: () => void;
  goBack: () => void;
  discard: () => void;
  setAnalysisProgress: (progress: AnalysisProgress[]) => void;
  setExtractResults: (
    results: ExtractResponse,
    initialSearchResult?: ColoredMaterialResult | null,
    initialSearchKey?: string | null,
  ) => void;
  setError: (error: string) => void;
}

const ExploreImageContext = createContext<ExploreImageContextValue | null>(
  null,
);

/**
 * Context provider for the upload-and-match flow. Owns the flow-step state
 * machine (`idle` → `region-selection` → `analysis` → `results`), the
 * uploaded image / regions / extract results, and the blob URL lifecycle
 * (revokes on re-upload, `DISCARD`, and unmount).
 *
 * Consumers must read state via {@link useExploreImage}.
 */
export const ExploreImageProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const imageRef = useRef(state.uploadedImage);
  imageRef.current = state.uploadedImage;

  useEffect(() => {
    return () => {
      if (imageRef.current) {
        URL.revokeObjectURL(imageRef.current);
      }
    };
  }, []);

  const setUploadedImage = useCallback(
    (
      file: File,
      image: string,
      naturalSize: { width: number; height: number },
    ) => {
      dispatch({
        type: "SET_UPLOADED_IMAGE",
        payload: { image, file, naturalSize },
      });
    },
    [],
  );

  const setRegions = useCallback((regions: Region[]) => {
    dispatch({ type: "SET_REGIONS", payload: regions });
  }, []);

  const addRegion = useCallback((region: Region, max: number) => {
    dispatch({ type: "ADD_REGION", payload: { region, max } });
  }, []);

  const updateRegion = useCallback((id: string, partial: Partial<Region>) => {
    dispatch({ type: "UPDATE_REGION", payload: { id, partial } });
  }, []);

  const removeRegion = useCallback((id: string) => {
    dispatch({ type: "REMOVE_REGION", payload: id });
  }, []);

  const confirmRegions = useCallback(() => {
    dispatch({ type: "SET_FLOW_STEP", payload: "analysis" });
  }, []);

  const goBack = useCallback(() => {
    if (state.flowStep === "results" || state.flowStep === "analysis") {
      dispatch({ type: "SET_FLOW_STEP", payload: "region-selection" });
    } else if (state.flowStep === "region-selection") {
      dispatch({ type: "DISCARD" });
    }
  }, [state.flowStep]);

  const discard = useCallback(() => {
    dispatch({ type: "DISCARD" });
  }, []);

  const setAnalysisProgress = useCallback((progress: AnalysisProgress[]) => {
    dispatch({ type: "SET_ANALYSIS_PROGRESS", payload: progress });
  }, []);

  const setExtractResults = useCallback(
    (
      results: ExtractResponse,
      initialSearchResult: ColoredMaterialResult | null = null,
      initialSearchKey: string | null = null,
    ) => {
      dispatch({
        type: "SET_EXTRACT_RESULTS",
        payload: {
          extractResults: results,
          initialSearchResult,
          initialSearchKey,
        },
      });
    },
    [],
  );

  const setError = useCallback((error: string) => {
    dispatch({ type: "SET_ERROR", payload: error });
  }, []);

  return (
    <ExploreImageContext.Provider
      value={{
        state,
        setUploadedImage,
        setRegions,
        addRegion,
        updateRegion,
        removeRegion,
        confirmRegions,
        goBack,
        discard,
        setAnalysisProgress,
        setExtractResults,
        setError,
      }}
    >
      {children}
    </ExploreImageContext.Provider>
  );
};

/**
 * Hook accessor for {@link ExploreImageContext}. Throws if called outside an
 * {@link ExploreImageProvider} — flow-step mutations and region editing all
 * require the provider.
 */
export const useExploreImage = () => {
  const context = useContext(ExploreImageContext);
  if (!context) {
    throw new Error(
      "useExploreImage must be used within an ExploreImageProvider",
    );
  }
  return context;
};
